// ===================================================================
// KAIXO - FeedItem
// ===================================================================
// Renderer de una publicación (latido) del feed. Incluye:
//  - Header clickable al perfil del canal
//  - Media (carrusel o foto/video suelto con fullscreen al tap)
//  - Texto con enlaces (linkify)
//  - Acciones IG-style (like / comentar / compartir)
//  - Comentarios inline (desplegables)
//  - Botones de contacto si el canal los habilita
//  - Termómetro de Latido Dorado
//
// Antes esto era ~125 líneas inline en ChannelsV2Modal dentro de
// feed.map(...). Ahora es un componente reutilizable con props
// nombradas. CERO cambios funcionales.
// ===================================================================

import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '../Icons/Icons';
import { linkify } from '../../utils/linkify';
import LatidoCarousel from './LatidoCarousel';
import InlineComments from './InlineComments';
import LatidoDoradoBar from '../LatidoDorado/LatidoDoradoBar';
import ReportModal from '../ReportModal/ReportModal';
import { pickId } from './utils';
import ct from './i18n';

const QUICK_REACTIONS = ['❤️', '🔥', '😂', '😮', '😢', '👍'];

export default function FeedItem({
    message,                // objeto m del feed
    currentUserId,          // uid
    channels,               // fallback para buscar inviteCode si no llega del backend

    // UI state consultivo (read-only):
    expandedComments,       // { [messageId]: 'loading' | Comment[] }
    inlineDraft,            // { [messageId]: string }
    commentTranslations,
    commentLikes,
    reactionPickerId,       // id del post con picker abierto

    // Acciones:
    onOpenProfile,          // (inviteCode) => void
    onOpenImage,            // (url) => void
    onOpenVideo,            // (url) => void
    onReact,                // (messageId, emoji) => void
    onToggleReactionPicker, // (messageId) => void
    onToggleComments,       // (messageId) => void
    onShare,                // (message) => void
    onDraftChange,          // (messageId, value) => void
    onSubmitComment,        // (messageId) => void
    onToggleTranslateComment,
    onToggleLikeComment,
    onDeleteComment,
    onChatWithAdmin,        // (ownerId) => void
    onInquiry               // (message, channelId) => void
}) {
    const m = message;
    const uid = String(currentUserId || '');
    const mode = m.effectiveContactMode || 'none';
    const ownerId = String(m.channelOwner || '');
    const isAuthor = ownerId === uid;
    const showDirect = !isAuthor && (mode === 'direct' || mode === 'both');
    const showForm = !isAuthor && (mode === 'form' || mode === 'both');

    const openProfile = () => {
        // channelInviteCode viene del backend — robusto incluso para canales
        // no suscritos. Fallback al handle o al lookup local por compat.
        const code = m.channelInviteCode
            || (m.channelHandle ? `@${m.channelHandle}` : null)
            || channels?.find?.(c => String(c._id) === String(m.channel))?.inviteCode;
        if (code) onOpenProfile(code);
    };

    // Agregados de reacciones: cada usuario solo puede tener 1 emoji por
    // post (regla de backend). Mostramos:
    //  - totalReactions: suma de TODAS las reacciones del post
    //  - myEmoji: el emoji que el usuario actual tiene puesto (si lo hay)
    // El botón del corazón muestra `myEmoji` si existe, sino un corazón
    // outline. Al tocarlo se abre el picker. Tocar el emoji ya puesto
    // de nuevo lo quita (toggle off).
    const reactionsObj = m.reactions && typeof m.reactions === 'object' ? m.reactions : {};
    let totalReactions = 0;
    let myEmoji = null;
    for (const [k, users] of Object.entries(reactionsObj)) {
        if (!Array.isArray(users) || users.length === 0) continue;
        totalReactions += users.length;
        if (!myEmoji && users.some(u => String(u) === uid)) {
            myEmoji = k;
        }
    }
    const commentCount = m.commentsCount || 0;
    const sharesCount = m.sharesCount || 0;
    const expanded = !!expandedComments?.[String(m._id)];
    const comments = expandedComments?.[String(m._id)];
    const pickerOpen = reactionPickerId === String(m._id);

    // Menú ⋮ y modal de denuncia (no se muestra si el post es del usuario)
    const [menuOpen, setMenuOpen] = useState(false);
    const [reporting, setReporting] = useState(false);
    const menuRef = useRef(null);
    useEffect(() => {
        if (!menuOpen) return;
        const handleOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [menuOpen]);

    const handleHeartTap = () => {
        if (myEmoji) {
            // Si ya tiene una reacción: primer tap quita (toggle off).
            onReact(String(m._id), myEmoji);
        } else {
            // Sin reacción: abrir picker para elegir.
            if (onToggleReactionPicker) onToggleReactionPicker(String(m._id));
        }
    };

    // Doble-tap = me gusta (estilo Instagram). Si ya hay reacción, mantiene
    // la que tenga (no quita). Muestra un corazón animado en el punto del tap.
    const lastTapRef = useRef({ t: 0, x: 0, y: 0 });
    const [heartBurst, setHeartBurst] = useState(null);
    const itemRootRef = useRef(null);
    const handleDoubleTapDetector = (e) => {
        // Ignorar taps sobre elementos interactivos — el doble-tap solo
        // aplica al área del post (foto/texto/fondo), no a botones, vídeos
        // con controles, comentarios, links, etc.
        const target = e.target;
        if (target?.closest && target.closest('button, a, input, textarea, video, audio, .v2-reaction-picker, .v2-feed-comments, .v2-ig-actions')) return;
        const t = (e.changedTouches && e.changedTouches[0]) || e;
        const now = Date.now();
        const dt = now - lastTapRef.current.t;
        const dx = Math.abs(t.clientX - lastTapRef.current.x);
        const dy = Math.abs(t.clientY - lastTapRef.current.y);
        if (dt < 350 && dx < 30 && dy < 30) {
            lastTapRef.current = { t: 0, x: 0, y: 0 };
            if (!myEmoji) onReact(String(m._id), '❤️');
            const root = itemRootRef.current;
            if (root) {
                const r = root.getBoundingClientRect();
                setHeartBurst({ x: t.clientX - r.left, y: t.clientY - r.top, key: now });
                setTimeout(() => setHeartBurst(prev => (prev && prev.key === now ? null : prev)), 850);
            }
        } else {
            lastTapRef.current = { t: now, x: t.clientX, y: t.clientY };
        }
    };

    return (
        <div
            ref={itemRootRef}
            className={`v2-feed-item ${isAuthor ? 'v2-feed-item-own' : ''} ${(m.isSponsored || m.isBoostedByMe) ? 'v2-feed-item-sponsored' : ''}`}
            data-post-id={String(m._id)}
            onTouchEnd={handleDoubleTapDetector}
            onDoubleClick={handleDoubleTapDetector}
        >
            {/* Header con avatar + nombre + badges */}
            <div className="v2-feed-head" onClick={openProfile}>
                <div
                    className="v2-feed-avatar"
                    onClick={e => {
                        if (m.channelAvatar) {
                            e.stopPropagation();
                            onOpenImage(m.channelAvatar);
                        }
                    }}
                >
                    {m.channelAvatar
                        ? <img src={m.channelAvatar} alt="" loading="lazy" decoding="async" style={{ cursor: 'pointer' }} />
                        : (m.channelName?.[0] || '?')}
                </div>
                <div className="v2-feed-meta">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <strong>{m.channelName}</strong>
                        {/* Antes había un badge "Tu canal" en posts propios.
                            El usuario lo retiró: ahora todos los posts llevan
                            el mismo menú ⋮ a la derecha (Denunciar dentro). */}
                        {isAuthor && m.isBoostedByMe && (
                            <span className="v2-boosted-badge">Impulsado</span>
                        )}
                        {m.isSponsored && (
                            <span className="v2-sponsored-badge">Publicidad</span>
                        )}
                    </div>
                    <div className="v2-feed-time">
                        {new Date(m.createdAt).toLocaleString()}
                    </div>
                </div>
                {/* Menú ⋮ en TODOS los posts (propios y ajenos) por
                    consistencia visual. En el feed de inicio el único item
                    es Denunciar. Las acciones Editar/Eliminar/Impulsar para
                    posts propios viven en el perfil del usuario, no aquí. */}
                <div className="v2-feed-menu" ref={menuRef}>
                    <button
                        className="v2-feed-menu-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setMenuOpen(o => !o);
                        }}
                        aria-label={ct.moreOptions || 'More options'}
                    >⋮</button>
                    {menuOpen && (
                        <div className="v2-feed-menu-dropdown" onClick={(e) => e.stopPropagation()}>
                            <button
                                className="v2-feed-menu-item"
                                onClick={() => {
                                    setMenuOpen(false);
                                    setReporting(true);
                                }}
                            >
                                🚩 {ct.reportPost || 'Denunciar'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Media: carrusel o medio suelto */}
            {(() => {
                if (Array.isArray(m.mediaUrls) && m.mediaUrls.length > 0) {
                    return (
                        <LatidoCarousel
                            media={m.mediaUrls}
                            onOpenImage={onOpenImage}
                            onOpenVideo={onOpenVideo}
                        />
                    );
                }
                if (m.mediaType === 'image' && m.mediaUrl) {
                    return (
                        <AdaptiveFeedImage
                            url={m.mediaUrl}
                            onClick={e => { e.stopPropagation(); onOpenImage(m.mediaUrl); }}
                        />
                    );
                }
                return null;
            })()}

            {/* Texto con enlaces */}
            {m.text && <div className="v2-feed-text">{linkify(m.text)}</div>}

            {/* Acciones IG-style: corazón (abre picker), comentar, compartir.
                Si el usuario ya eligió un emoji, el botón lo muestra en
                vez del corazón; tap vuelve a quitarlo. */}
            <div className="v2-ig-actions">
                <button
                    className={`v2-ig-icon-btn v2-ig-heart ${myEmoji ? 'liked' : ''}`}
                    onClick={handleHeartTap}
                    title={myEmoji ? 'Quitar reacción' : 'Reaccionar'}
                    aria-label="Reaccionar"
                >
                    {myEmoji ? (
                        <span className="v2-ig-heart-emoji">{myEmoji}</span>
                    ) : (
                        <svg width="24" height="24" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                    )}
                </button>
                {totalReactions > 0 && <span className="v2-ig-count">{totalReactions}</span>}

                <button
                    className={`v2-ig-icon-btn ${expanded ? 'active' : ''}`}
                    onClick={() => onToggleComments(m._id)}
                    aria-label="Comentarios"
                >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    </svg>
                </button>
                {commentCount > 0 && <span className="v2-ig-count">{commentCount}</span>}

                <button
                    className="v2-ig-icon-btn"
                    onClick={() => onShare(m)}
                    aria-label="Compartir"
                >
                    <Icons.Share size={20} />
                </button>
                {sharesCount > 0 && <span className="v2-ig-count">{sharesCount}</span>}
            </div>

            {/* Picker de reacciones extras */}
            {pickerOpen && (
                <div className="v2-reaction-picker">
                    {QUICK_REACTIONS.map(e => (
                        <button
                            key={e}
                            className="v2-reaction-emoji"
                            onClick={() => {
                                onReact(String(m._id), e);
                                onToggleReactionPicker && onToggleReactionPicker(null);
                            }}
                        >{e}</button>
                    ))}
                </div>
            )}

            {/* Comentarios inline desplegables */}
            {expanded && (
                <InlineComments
                    messageId={m._id}
                    comments={comments}
                    commentsEnabled={m.commentsEnabled}
                    isAuthor={isAuthor}
                    currentUserId={uid}
                    draft={inlineDraft?.[String(m._id)] || ''}
                    onDraftChange={(v) => onDraftChange(m._id, v)}
                    onSubmit={() => onSubmitComment(m._id)}
                    commentTranslations={commentTranslations}
                    commentLikes={commentLikes}
                    onToggleTranslate={onToggleTranslateComment}
                    onToggleLike={onToggleLikeComment}
                    onDelete={onDeleteComment}
                />
            )}

            {/* Botones de contacto (si el canal tiene modos activos) */}
            {(showDirect || showForm) && (
                <div className="v2-contact-row">
                    {showDirect && (
                        <button
                            className="v2-contact-btn"
                            onClick={() => onChatWithAdmin(ownerId)}
                        >
                            <Icons.Send size={13} color="white" /> {ct.chatWithAdmin}
                        </button>
                    )}
                    {showForm && (
                        <button
                            className="v2-contact-btn"
                            onClick={() => onInquiry(m, pickId(m.channel))}
                        >
                            <Icons.Edit size={13} color="white" /> {ct.sendInquiry}
                        </button>
                    )}
                </div>
            )}

            {/* Termómetro de Latido Dorado */}
            <LatidoDoradoBar postId={String(m._id)} reactions={m.reactions} />

            {reporting && (
                <ReportModal
                    targetType="post"
                    targetId={String(m._id)}
                    targetUserId={ownerId}
                    targetUserName={m.channelName}
                    snapshot={{
                        text: m.text || '',
                        mediaUrls: Array.isArray(m.mediaUrls)
                            ? m.mediaUrls
                            : (m.mediaUrl ? [m.mediaUrl] : [])
                    }}
                    onClose={() => setReporting(false)}
                />
            )}

            {/* Burst del corazón en el punto del doble-tap. Se quita solo
                tras la animación. position:absolute respecto al item root. */}
            {heartBurst && (
                <div
                    key={heartBurst.key}
                    className="v2-double-tap-heart"
                    style={{ left: heartBurst.x, top: heartBurst.y }}
                    aria-hidden
                >❤️</div>
            )}
        </div>
    );
}

// ───────────────────────────────────────────────────────────
// AdaptiveFeedImage
// ───────────────────────────────────────────────────────────
// Mide la imagen al cargar (naturalWidth / naturalHeight) y elige
// el aspect-ratio del wrap. El rango cubre los formatos nativos de
// cámara de móvil (sensor 4:3 disparado en vertical o horizontal):
//   - 3:4 (0.75) → foto vertical de iPhone/Android (95% de uploads)
//   - 1:1 (1.00) → cuadrada (modo cámara cuadrada / IG legacy)
//   - 4:3 (1.33) → foto horizontal de iPhone/Android
//
// Dentro del rango: el wrap toma el ratio exacto → llena perfecto.
// Fuera (panorámicas 16:9, capturas 9:16, etc.): clamp al extremo y
// halo blur de la propia imagen rellena los huecos.
const MIN_RATIO_PORTRAIT = 0.75;  // 3:4 — vertical de cámara móvil
const MAX_RATIO_LANDSCAPE = 1.34; // ~4:3 — horizontal de cámara móvil

function AdaptiveFeedImage({ url, onClick }) {
    const [ratio, setRatio] = useState(null); // null hasta cargar
    const handleLoad = (e) => {
        const w = e.target?.naturalWidth;
        const h = e.target?.naturalHeight;
        if (!w || !h) return;
        setRatio(w / h);
    };
    // Mientras carga usamos 4/5 como placeholder para no causar layout shift drástico.
    const naturalRatio = ratio == null ? 0.8 : ratio;
    const clampedRatio = Math.max(MIN_RATIO_PORTRAIT, Math.min(MAX_RATIO_LANDSCAPE, naturalRatio));
    const showBlurFill = ratio != null && (ratio < MIN_RATIO_PORTRAIT || ratio > MAX_RATIO_LANDSCAPE);

    return (
        <div
            className="v2-feed-image-wrap"
            style={{ aspectRatio: clampedRatio }}
            onClick={onClick}
        >
            {showBlurFill && (
                <img
                    src={url}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    className="v2-feed-image-bg"
                />
            )}
            <img
                src={url}
                alt=""
                loading="lazy"
                decoding="async"
                className="v2-feed-image"
                onLoad={handleLoad}
            />
        </div>
    );
}
